import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Search, FileDown, User, Building, FileText, DollarSign, Eye, Clock, Filter, RefreshCw } from 'lucide-react';
import axios from 'axios';
import Chart from '../Chart/Chart';
import ItemLists from '../ItemLists/ItemLists';
import Navbar from '../Navbar/Navbar';
import ProgressBar from '../ProgressBar/ProgressBar';
import Sidebar from '../Sidebar/Sidebar';
import TableList from '../TableList/TableList';
import './Home.scss';
import * as XLSX from 'xlsx';

function Home() {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [activeTableTab, setActiveTableTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    
    // Fetch data for all tabs on component mount or refresh trigger
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [usersResponse, clientsResponse, propertiesResponse] = await Promise.all([
                    axios.get("https://portal-sanieldanproperties-api.onrender.com/adminss/users"),
                    axios.get("https://portal-sanieldanproperties-api.onrender.com/adminss/clients"),
                    axios.get("https://portal-sanieldanproperties-api.onrender.com/adminss/properties")
                ]);
                
                setUsers(usersResponse.data);
                setClients(clientsResponse.data);
                setProperties(propertiesResponse.data);
            } catch (error) {
                console.log('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [refreshTrigger]);
    
    // Action handlers for different entities
    const handleViewUser = async (userId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/users/${userId}`);
            setModalTitle('User Details');
            setModalContent(
                <div className="p-4">
                    <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/3 p-3">
                            <div className="text-center">
                                <img 
                                    src={response.data.image || 'https://via.placeholder.com/150'} 
                                    alt={`${response.data.firstName} ${response.data.lastName}`}
                                    className="rounded-full w-32 h-32 mx-auto mb-4 object-cover"
                                />
                                <h3 className="text-xl font-bold">{`${response.data.firstName} ${response.data.lastName}`}</h3>
                                <p className="text-gray-600">{response.data.email}</p>
                                <p className="text-gray-600">{response.data.phoneNumber}</p>
                            </div>
                        </div>
                        <div className="md:w-2/3 p-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-semibold">Address</h4>
                                    <p>{response.data.address || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">City</h4>
                                    <p>{response.data.city || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">State</h4>
                                    <p>{response.data.state || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Country</h4>
                                    <p>{response.data.country || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Gender</h4>
                                    <p>{response.data.gender || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Date of Birth</h4>
                                    <p>{response.data.dob || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Bank Name</h4>
                                    <p>{response.data.bankName || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Account Number</h4>
                                    <p>{response.data.bankNumber || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Account Name</h4>
                                    <p>{response.data.bankFullName || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Balance</h4>
                                    <p>{response.data.balance || '0'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Unique Code</h4>
                                    <p>{response.data.uniqueCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Joined Date</h4>
                                    <p>{new Date(response.data.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };
    
    const handleUserTransactions = async (userId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/users/${userId}/transactions`);
            setModalTitle('User Transactions');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Property
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Total Cost
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Amount Paid
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Balance
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((transaction, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {new Date(transaction.dateRegistered).toLocaleDateString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.property}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${transaction.totalCost.toLocaleString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${transaction.amountPaid.toLocaleString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${transaction.balance.toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500">
                                        No transactions found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching user transactions:', error);
        }
    };
    
    const handleUserClients = async (userId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/users/${userId}/clients`);
            setModalTitle('User\'s Clients');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Phone
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Location
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((client) => (
                                    <tr key={client._id}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {`${client.firstName} ${client.lastName}`}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {client.email}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {client.phone}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {`${client.city}, ${client.state}`}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            <span className={`px-2 py-1 rounded text-xs ${
                                                client.status === 'Active' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {client.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500">
                                        No clients found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching user clients:', error);
        }
    };
    
    const handleUserCommissions = async (userId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/users/${userId}/commissions`);
            setModalTitle('User Commissions');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date Received
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Property
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Commission Paid
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((commission, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {new Date(commission.dateReceived).toLocaleDateString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {commission.property}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${commission.commissionPaid.toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="py-4 px-4 text-center text-gray-500">
                                        No commissions found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching user commissions:', error);
        }
    };

    const handleViewClient = async (clientId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/clients/${clientId}`);
            setModalTitle('Client Details');
            setModalContent(
                <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <h4 className="font-semibold">Personal Information</h4>
                            <div className="mt-2 space-y-2">
                                <p><span className="font-medium">Name:</span> {`${response.data.firstName} ${response.data.lastName}`}</p>
                                <p><span className="font-medium">Email:</span> {response.data.email}</p>
                                <p><span className="font-medium">Phone:</span> {response.data.phone}</p>
                                <p><span className="font-medium">Gender:</span> {response.data.gender}</p>
                                <p><span className="font-medium">Date of Birth:</span> {new Date(response.data.dateOfBirth).toLocaleDateString()}</p>
                                <p><span className="font-medium">Occupation:</span> {response.data.occupation}</p>
                            </div>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold">Address Information</h4>
                            <div className="mt-2 space-y-2">
                                <p><span className="font-medium">Address:</span> {response.data.address}</p>
                                <p><span className="font-medium">City:</span> {response.data.city}</p>
                                <p><span className="font-medium">State:</span> {response.data.state}</p>
                                <p><span className="font-medium">Country:</span> {response.data.country}</p>
                                <p><span className="font-medium">Zip Code:</span> {response.data.zipCode || 'N/A'}</p>
                                <p><span className="font-medium">Office Address:</span> {response.data.officeAddress}</p>
                            </div>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold">Next of Kin Information</h4>
                            <div className="mt-2 space-y-2">
                                <p><span className="font-medium">Name:</span> {response.data.nextOfKinFullName}</p>
                                <p><span className="font-medium">Relationship:</span> {response.data.nextOfKinRelationship}</p>
                                <p><span className="font-medium">Email:</span> {response.data.nextOfKinEmail || 'N/A'}</p>
                                <p><span className="font-medium">Phone:</span> {response.data.nextOfKinPhone}</p>
                            </div>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold">Additional Information</h4>
                            <div className="mt-2 space-y-2">
                                <p>
                                    <span className="font-medium">Status:</span> 
                                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                        response.data.status === 'Active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {response.data.status}
                                    </span>
                                </p>
                                <p><span className="font-medium">Registered Date:</span> {new Date(response.data.createdAt).toLocaleDateString()}</p>
                                <p><span className="font-medium">Realtor:</span> {response.data.realtor?.firstName} {response.data.realtor?.lastName}</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching client details:', error);
        }
    };
    
    const handleClientProperties = async (clientId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/clients/${clientId}/properties`);
            setModalTitle('Client Properties');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Property
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Location
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Price
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Units Purchased
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date Purchased
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((property, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {property.producttitle}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {property.productlocation}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${property.productprice}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {property.unitsPurchased}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {new Date(property.purchaseDate).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500">
                                        No properties found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching client properties:', error);
        }
    };
    
    const handleClientTransactions = async (clientId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/clients/${clientId}/transactions`);
            setModalTitle('Client Transactions');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Property
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Units
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Amount Paid
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Balance
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((transaction, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.dateOfPay}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.productName}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.numberOfUnits}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${Number(transaction.amountPaid).toLocaleString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${Number(transaction.balance).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="py-4 px-4 text-center text-gray-500">
                                        No transactions found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching client transactions:', error);
        }
    };
    
    const handleViewProperty = async (propertyId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/properties/${propertyId}`);
            setModalTitle('Property Details');
            setModalContent(
                <div className="p-4">
                    <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/3 p-3">
                            <img 
                                src={response.data.productimage} 
                                alt={response.data.producttitle}
                                className="w-full h-auto rounded-lg shadow-md"
                            />
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <h4 className="font-semibold text-lg">{response.data.producttitle}</h4>
                                <p className="text-gray-700 mt-2">{response.data.productdescription}</p>
                            </div>
                        </div>
                        <div className="md:w-2/3 p-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-semibold">Price</h4>
                                    <p className="text-xl font-bold text-green-600">${response.data.productprice}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Location</h4>
                                    <p>{response.data.productlocation}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Units Sold</h4>
                                    <p>{response.data.productunitssold || '0'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Units Left</h4>
                                    <p>{response.data.productunitsleft || '0'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Direct Commission</h4>
                                    <p>{response.data.directcommission || '0%'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Indirect Commission</h4>
                                    <p>{response.data.inDirectcommission || '0%'}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Created At</h4>
                                    <p>{new Date(response.data.createdAt).toLocaleDateString()}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold">Product Form</h4>
                                    <a 
                                        href={response.data.productformfile} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                    >
                                        Download Form
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching property details:', error);
        }
    };
    
    const handlePropertyTransactions = async (propertyId) => {
        try {
            const response = await axios.get(`https://portal-sanieldanproperties-api.onrender.com/adminss/properties/${propertyId}/transactions`);
            setModalTitle('Property Transactions');
            setModalContent(
                <div className="p-4">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Buyer
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Mobile
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Units
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Amount Paid
                                </th>
                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Balance
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.data.length > 0 ? (
                                response.data.map((transaction, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.dateOfPay}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.nameofbuyer}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.mobileNumber}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            {transaction.numberOfUnits}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${Number(transaction.amountPaid).toLocaleString()}
                                        </td>
                                        <td className="py-2 px-4 border-b border-gray-200">
                                            ${Number(transaction.balance).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="py-4 px-4 text-center text-gray-500">
                                        No transactions found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
            setModalOpen(true);
        } catch (error) {
            console.error('Error fetching property transactions:', error);
        }
    };
    
    // Export functions for different entities
    const exportToExcel = async (data, sheetName, fileName) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };
    
    const handleExportUsers = () => {
        // Prepare user data for export (removing sensitive info)
        const exportData = users.map(user => ({
            'First Name': user.firstName,
            'Last Name': user.lastName,
            'Email': user.email,
            'Phone': user.phoneNumber,
            'Address': user.address,
            'City': user.city,
            'State': user.state,
            'Country': user.country,
            'Balance': user.balance,
            'Referred Users': user.referredUsers?.length || 0,
            'Registration Date': new Date(user.createdAt).toLocaleDateString()
        }));
        
        exportToExcel(exportData, 'Users', 'users_export');
    };
    
    const handleExportClients = () => {
        // Prepare client data for export
        const exportData = clients.map(client => ({
            'First Name': client.firstName,
            'Last Name': client.lastName,
            'Email': client.email,
            'Phone': client.phone,
            'Gender': client.gender,
            'DOB': new Date(client.dateOfBirth).toLocaleDateString(),
            'Address': client.address,
            'City': client.city,
            'State': client.state,
            'Country': client.country,
            'Occupation': client.occupation,
            'Status': client.status,
            'Registration Date': new Date(client.createdAt).toLocaleDateString()
        }));
        
        exportToExcel(exportData, 'Clients', 'clients_export');
    };
    
    const handleExportProperties = () => {
        // Prepare property data for export
        const exportData = properties.map(property => ({
            'Title': property.producttitle,
            'Description': property.productdescription,
            'Price': property.productprice,
            'Location': property.productlocation,
            'Units Sold': property.productunitssold,
            'Units Left': property.productunitsleft,
            'Direct Commission': property.directcommission,
            'Indirect Commission': property.inDirectcommission,
            'Created Date': new Date(property.createdAt).toLocaleDateString()
        }));
        
        exportToExcel(exportData, 'Properties', 'properties_export');
    };
    
    // Search function for each entity
    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return (
            user.firstName?.toLowerCase().includes(searchLower) ||
            user.lastName?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.phoneNumber?.includes(searchTerm) ||
            user.username?.toLowerCase().includes(searchLower) ||
            user.city?.toLowerCase().includes(searchLower) ||
            user.state?.toLowerCase().includes(searchLower)
        );
    });
    
    const filteredClients = clients.filter(client => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return (
            client.firstName?.toLowerCase().includes(searchLower) ||
            client.lastName?.toLowerCase().includes(searchLower) ||
            client.email?.toLowerCase().includes(searchLower) ||
            client.phone?.includes(searchTerm) ||
            client.city?.toLowerCase().includes(searchLower) ||
            client.state?.toLowerCase().includes(searchLower) ||
            client.occupation?.toLowerCase().includes(searchLower)
        );
    });
    
    const filteredProperties = properties.filter(property => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return (
            property.producttitle?.toLowerCase().includes(searchLower) ||
            property.productdescription?.toLowerCase().includes(searchLower) ||
            property.productprice?.toString().includes(searchTerm) ||
            property.productlocation?.toLowerCase().includes(searchLower)
        );
    });

    // Component for the Data Table Tab System
    const DataTables = () => {
        return (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTableTab('users')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                                activeTableTab === 'users'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center justify-center">
                                <User size={16} className="mr-2" />
                                Users
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTableTab('clients')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                                activeTableTab === 'clients'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center justify-center">
                                <User size={16} className="mr-2" />
                                Clients
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTableTab('properties')}
                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                                activeTableTab === 'properties'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <div className="flex items-center justify-center">
                                <Building size={16} className="mr-2" />
                                Properties
                            </div>
                        </button>
                    </nav>
                </div>

                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">
                            {activeTableTab === 'users' && 'Users Management'}
                            {activeTableTab === 'clients' && 'Clients Management'}
                            {activeTableTab === 'properties' && 'Properties Management'}
                        </h2>
                        <div className="flex items-center space-x-2">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder={`Search ${activeTableTab}...`}
                                    className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            </div>
                            <Button 
                                variant="light" 
                                className="flex items-center border"
                                onClick={() => setRefreshTrigger(prev => prev + 1)}
                                title="Refresh Data"
                            >
                                <RefreshCw size={18} />
                            </Button>
                            <Button 
                                variant="success" 
                                className="flex items-center"
                                onClick={
                                    activeTableTab === 'users' 
                                        ? handleExportUsers 
                                        : activeTableTab === 'clients'
                                            ? handleExportClients
                                            : handleExportProperties
                                }
                            >
                                <FileDown size={18} className="mr-1" />
                                Export
                            </Button>
                        </div>
                    </div>

                    {/* Users Table */}
                    {activeTableTab === 'users' && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Phone
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Location
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Referrals
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Balance
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Join Date
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-6">
                                                <div className="flex justify-center items-center">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                                    <span className="ml-2">Loading users...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => (
                                            <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex items-center">
                                                        <div className="w-8 h-8 mr-3 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                                                            {user.image ? (
                                                                <img 
                                                                    src={user.image} 
                                                                    alt={`${user.firstName} ${user.lastName}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-center w-full h-full bg-blue-500 text-white">
                                                                    {user.firstName?.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold">{`${user.firstName} ${user.lastName}`}</p>
                                                            <p className="text-sm text-gray-500">{user.username || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {user.email}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {user.phoneNumber}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {user.city && user.state ? `${user.city}, ${user.state}` : 'N/A'}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                        {user.referredUsers ? user.referredUsers.length : 0}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200 font-medium">
                                                    {user.balance ? `$${Number(user.balance).toLocaleString()}` : '$0'}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {new Date(user.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex space-x-2">
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-blue-100 text-blue-600"
                                                            onClick={() => handleViewUser(user._id)}
                                                            title="View Details"
                                                        >
                                                            <Eye size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-green-100 text-green-600"
                                                            onClick={() => handleUserTransactions(user._id)}
                                                            title="Transactions"
                                                        >
                                                            <FileText size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-purple-100 text-purple-600"
                                                            onClick={() => handleUserClients(user._id)}
                                                            title="Clients"
                                                        >
                                                            <User size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-amber-100 text-amber-600"
                                                            onClick={() => handleUserCommissions(user._id)}
                                                            title="Commissions"
                                                        >
                                                            <DollarSign size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-6 text-gray-500">
                                                No users found matching your search
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Clients Table */}
                    {activeTableTab === 'clients' && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Phone
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Gender
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Location
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Occupation
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-6">
                                                <div className="flex justify-center items-center">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                                    <span className="ml-2">Loading clients...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredClients.length > 0 ? (
                                        filteredClients.map((client) => (
                                            <tr key={client._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="py-3 px-4 border-b border-gray-200 font-medium">
                                                    {`${client.firstName} ${client.lastName}`}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {client.email}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {client.phone}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {client.gender}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {`${client.city}, ${client.state}`}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {client.occupation}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${
                                                        client.status === 'Active' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {client.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex space-x-2">
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-blue-100 text-blue-600"
                                                            onClick={() => handleViewClient(client._id)}
                                                            title="View Details"
                                                        >
                                                            <Eye size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-green-100 text-green-600"
                                                            onClick={() => handleClientProperties(client._id)}
                                                            title="Properties"
                                                        >
                                                            <Building size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-amber-100 text-amber-600"
                                                            onClick={() => handleClientTransactions(client._id)}
                                                            title="Transactions"
                                                        >
                                                            <FileText size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-6 text-gray-500">
                                                No clients found matching your search
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Properties Table */}
                    {activeTableTab === 'properties' && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Property
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Price
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Location
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Units Sold/Left
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Commission Rates
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Date Added
                                        </th>
                                        <th className="py-3 px-4 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-6">
                                                <div className="flex justify-center items-center">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                                    <span className="ml-2">Loading properties...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredProperties.length > 0 ? (
                                        filteredProperties.map((property) => (
                                            <tr key={property._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex items-center">
                                                        <div className="w-12 h-12 mr-3 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                                                            <img 
                                                                src={property.productimage} 
                                                                alt={property.producttitle}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold">{property.producttitle}</p>
                                                            <p className="text-sm text-gray-500 truncate w-40" title={property.productdescription}>
                                                                {property.productdescription?.substring(0, 50)}
                                                                {property.productdescription?.length > 50 ? '...' : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200 font-medium text-green-600">
                                                    ${Number(property.productprice).toLocaleString()}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {property.productlocation || 'N/A'}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium">Sold: </span>
                                                            <span className="ml-1 text-sm">{property.productunitssold || '0'}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium">Left: </span>
                                                            <span className="ml-1 text-sm">{property.productunitsleft || '0'}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium">Direct: </span>
                                                            <span className="ml-1 text-sm">{property.directcommission || '0%'}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium">Indirect: </span>
                                                            <span className="ml-1 text-sm">{property.inDirectcommission || '0%'}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    {new Date(property.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4 border-b border-gray-200">
                                                    <div className="flex space-x-2">
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-blue-100 text-blue-600"
                                                            onClick={() => handleViewProperty(property._id)}
                                                            title="View Details"
                                                        >
                                                            <Eye size={18} />
                                                        </button>
                                                        <button 
                                                            className="p-1 rounded-full hover:bg-amber-100 text-amber-600"
                                                            onClick={() => handlePropertyTransactions(property._id)}
                                                            title="Transactions"
                                                        >
                                                            <Clock size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-6 text-gray-500">
                                                No properties found matching your search
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    return (
        <div className="home">
            <div className="home_sidebar">
                <Sidebar />
            </div>

            <div className="home_main">
                <div className="bg_color" />

                <div className="p-4">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-4"
                    >
                        <Tab eventKey="dashboard" title={
                            <div className="flex items-center">
                              
                            </div>
                        }>
                            <div className="home_items">
                                <ItemLists type="user" />
                                <ItemLists type="products" />
                                <ItemLists type="verified" />
                                <ItemLists type="pending" />
                                <ItemLists type="rejected" />
                            </div>
                            
                           
                        </Tab>
                        
                        <Tab eventKey="dataManagement" title={
                            <div className="flex items-center">
                               
                                
                            </div>
                        }>
                            <DataTables />
                        </Tab>
                        
                        <Tab eventKey="analytics" title={
                            <div className="flex items-center">
                               
                            </div>
                        }>
                            <div className="bg-white rounded-lg shadow-lg p-6">  
                              
                               
                                
                                <div className="mt-8">
                                    <h3 className="text-lg font-semibold mb-4">Performance Overview</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <h4 className="font-medium mb-3">Top Performing Users</h4>
                                            <div className="space-y-3">
                                                {users.slice(0, 5).map((user, index) => (
                                                    <div key={index} className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm mr-2">
                                                                {user.firstName?.charAt(0)}
                                                            </div>
                                                            <span>{`${user.firstName} ${user.lastName}`}</span>
                                                        </div>
                                                        <span className="font-medium">{user.referredUsers ? user.referredUsers.length : 0} clients</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <h4 className="font-medium mb-3">Top Selling Properties</h4>
                                            <div className="space-y-3">
                                                {properties.slice(0, 5).map((property, index) => (
                                                    <div key={index} className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="w-8 h-8 rounded overflow-hidden mr-2">
                                                                <img 
                                                                    src={property.productimage} 
                                                                    alt={property.producttitle}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                            <span className="truncate max-w-xs">{property.producttitle}</span>
                                                        </div>
                                                        <span className="font-medium">{property.productunitssold || 0} units</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            </div>
            
            {/* Modal Component */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <motion.div 
                        className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
                            <h3 className="text-xl font-semibold">{modalTitle}</h3>
                            <button 
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                onClick={() => setModalOpen(false)}
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-content">
                            {modalContent}
                        </div>
                        <div className="border-t p-4 text-right sticky bottom-0 bg-white">
                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                                onClick={() => setModalOpen(false)}
                            >
                                Close
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}

export default Home;